// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {isEllipsisActive} from 'common/util/dom';

import {ClampedTextAttrs, ClampedTextState, TruncatedStatus} from './types';
import {template} from './template';
import css from './index.inline.styl';

export interface TruncatedTextJsxProps {
  attrs: ClampedTextAttrs;
  on?: EmptyObj;
  props?: EmptyObj;
}

export class ClampedText extends Component<ClampedTextState, ClampedTextAttrs, unknown, unknown, unknown> {
  private collapsingDiv: HTMLElement = null;

  static get attrsSchema(): AttrsSchema<ClampedTextAttrs> {
    return {
      ...super.attrsSchema,
      text: `string`,
      lines: {type: `number`, default: 1},
    };
  }

  get config() {
    return {
      css,
      useShadowDom: true,
      template,
      defaultState: {status: TruncatedStatus.Unknown} as ClampedTextState,
      helpers: {
        maybeToggleExpandStatus: () => {
          // This check allows the user to select text WITHOUT toggling truncation.
          // Only toggle if there is no selection.
          if (window.getSelection().toString()) {
            return;
          }
          this.helpers.toggleExpandStatus();
        },
        toggleExpandStatus: (ev?: MouseEvent) => {
          if (ev) {
            ev.stopPropagation();
          }
          if (this.state.status === TruncatedStatus.Collapsed) {
            this.update({status: TruncatedStatus.Expanded});
          } else if (this.state.status === TruncatedStatus.Expanded) {
            this.update({status: TruncatedStatus.Collapsed});
          }
        },
        setElmRef: (elm: HTMLElement) => {
          this.collapsingDiv = elm;
          if (this.state.status === TruncatedStatus.Unknown) {
            window.requestAnimationFrame(() =>
              this.update({
                status: isEllipsisActive(this.collapsingDiv) ? TruncatedStatus.Collapsed : TruncatedStatus.None,
              }),
            );
          }
        },
      },
    };
  }

  attributeChangedCallback(attr: string, oldValue: string, newValue: string) {
    super.attributeChangedCallback(attr, oldValue, newValue);
    if (this.initialized && attr === `text` && oldValue !== newValue) {
      this.recalculateTruncation();
    }
  }

  connectedCallback() {
    super.connectedCallback();

    let windowSize = window.innerWidth;

    const handleResize = () => {
      const maybeNowNeedsTruncated = this.state.status === TruncatedStatus.None && window.innerWidth < windowSize;
      const maybeNowDoesNotNeedTruncated =
        [TruncatedStatus.Collapsed, TruncatedStatus.Expanded].includes(this.state.status) &&
        window.innerWidth > windowSize;

      if (maybeNowDoesNotNeedTruncated || maybeNowNeedsTruncated) {
        this.recalculateTruncation();
      }
      windowSize = window.innerWidth;
    };

    window.addEventListener(`resize`, handleResize);
    this.onDisconnected(() => window.removeEventListener(`resize`, handleResize));
  }

  recalculateTruncation() {
    const currentStatus = this.state.status;
    this.update({status: TruncatedStatus.Unknown});
    window.requestAnimationFrame(() => {
      const hasEllipsis = isEllipsisActive(this.collapsingDiv);
      if ([TruncatedStatus.Collapsed, TruncatedStatus.Expanded].includes(currentStatus) && hasEllipsis) {
        this.update({status: currentStatus});
      } else {
        this.update({
          status: hasEllipsis ? TruncatedStatus.Collapsed : TruncatedStatus.None,
        });
      }
    });
  }
}

export const ClampedTextEl = defineAndMakeJsxFactory<TruncatedTextJsxProps>(`mp-clamped-text`, ClampedText);
export const ClampedTextReact = panel2react<TruncatedTextJsxProps>(`mp-clamped-text`, ClampedText);
